import moment from "moment-timezone"

import { PayerCoverageCheckNetwork } from "@common-shared/config/coverage.config"
import type {
    AvailableInsuranceType,
    AvailablePlanType,
} from "@common-shared/config/coverage.config"

import { UtilHelper } from "@front-shared/helpers/util.helper.ts"
import type { ISelectRawOption } from "@front-shared/types/objects.types.ts"

import { CHECKER_CONFIG } from "../config/checker.config"
import { COVERAGE_CONFIG } from "../config/coverage.config"
import { GENERAL_CONFIG } from "../config/general.config.ts"
import { NETWORK_STATUS_CONFIG } from "../config/networkStatus.config"
import {
    INetworkStatusRuleDisplayConfig,
    INetworkStatusRulesFilters,
    NetworkStatusRuleDisplayFieldKey,
    NetworkStatusRuleRow,
} from "../types/networkStatus.types"
import { PoliciesListHelper } from "./policiesList.helper"

export class NetworkStatusHelper {
    /**
     * Get rules filters mapped for display (chips with label + value).
     */
    static getMappedDisplayFiltersForRules(
        filters: INetworkStatusRulesFilters,
    ): ISelectRawOption[] {
        const keys = Object.keys(
            filters,
        ) as (keyof INetworkStatusRulesFilters)[]

        const result: ISelectRawOption[] = []

        for (const key of keys) {
            // Display-only keys; chip is shown via payerId/thirdPartyAdministratorId
            if (key === "payer" || key === "thirdPartyAdministrator") {
                continue
            }

            let value: string | number | undefined | null = filters[key] as
                | string
                | number
                | undefined
                | null

            if (value === undefined || value === null || value === "") {
                continue
            }

            switch (key) {
                case "startDate":
                case "endDate":
                    const range = filters[key]

                    if (
                        !range ||
                        typeof range !== "object" ||
                        !("type" in range)
                    ) {
                        continue
                    }

                    const infiniteDateFallback = "∞"

                    const { from, to } =
                        PoliciesListHelper.getRelativeDateRangeValues(range)

                    value = `${from || infiniteDateFallback} - ${
                        to || infiniteDateFallback
                    }`

                    break

                case "networkStatus":
                    value =
                        value === PayerCoverageCheckNetwork.IN
                            ? (COVERAGE_CONFIG.payerCoverageCheckNetworkMapped
                                  .IN?.displayValue ?? "INN")
                            : value === PayerCoverageCheckNetwork.OUT
                              ? (COVERAGE_CONFIG.payerCoverageCheckNetworkMapped
                                    .OUT?.displayValue ?? "OON")
                              : String(value)

                    break

                case "payerId":
                case "thirdPartyAdministratorId":
                    const storedName =
                        key === "payerId"
                            ? filters.payer?.insuranceName
                            : filters.thirdPartyAdministrator?.insuranceName

                    if (storedName) {
                        value = storedName
                    }

                    break

                case "insuranceTypes":
                    value = Array.isArray(value)
                        ? (value as AvailableInsuranceType[]).join(", ")
                        : String(value)

                    break

                case "planTypes":
                    value = Array.isArray(value)
                        ? (value as AvailablePlanType[])
                              .map(t => CHECKER_CONFIG.planTypeMapping[t] ?? t)
                              .join(", ")
                        : String(value)

                    break

                case "providerNpi": {
                    value = NetworkStatusHelper.getProviderNpiDisplayValue(
                        String(value),
                    )

                    break
                }

                case "order":
                    value = String(value)

                    break

                default:
                    if (typeof value === "object" && value !== null) {
                        value = JSON.stringify(value)
                    } else {
                        value = String(value)
                    }
            }

            const label =
                NETWORK_STATUS_CONFIG.rulesFiltersDisplayLabels[key] ?? key

            result.push({
                key,
                label,
                value: String(value),
            })
        }

        return result
    }

    /**
     * Stable key for a rule row in the table (list: rule id, review: rule index).
     */
    static getRuleRowKey(rule: NetworkStatusRuleRow, index: number): string {
        if ("id" in rule) {
            return `rule-${rule.id}-${index}`
        }

        const idx =
            "index" in rule && typeof rule.index === "number"
                ? rule.index
                : index

        return `review-${idx}-${index}`
    }

    /**
     * Default (empty) rules filters.
     */
    static getDefaultRulesFilters(): INetworkStatusRulesFilters {
        return {}
    }

    /**
     * Remove empty/undefined values so only applied filters are sent to the API.
     * Ensures payerId/thirdPartyAdministratorId are set from payer/thirdPartyAdministrator
     * when present (form stores full objects like policies), and keeps objects for display.
     */
    static sanitizeRulesFilters(
        data: INetworkStatusRulesFilters,
    ): INetworkStatusRulesFilters {
        const result: INetworkStatusRulesFilters = {}

        // Derive ids from objects when present (form submits payer/TPA objects).
        // Do not fallback to data.payerId/data.thirdPartyAdministratorId so that
        // set -> remove still clears the id (avoids sending stale selected ID).

        if (data.payer) {
            result.payer = data.payer

            result.payerId = data.payer.payerId
        }

        if (data.thirdPartyAdministrator) {
            result.thirdPartyAdministrator = data.thirdPartyAdministrator

            result.thirdPartyAdministratorId =
                data.thirdPartyAdministrator.payerId
        }

        const keys = Object.keys(data) as (keyof INetworkStatusRulesFilters)[]

        for (const key of keys) {
            if (
                key === "payerId" ||
                key === "payer" ||
                key === "thirdPartyAdministratorId" ||
                key === "thirdPartyAdministrator"
            ) {
                continue
            }

            const value = data[key]

            if (
                value === undefined ||
                value === null ||
                (typeof value === "string" && !value.trim()) ||
                (Array.isArray(value) && !value.length) ||
                UtilHelper.isEmptyObject(value)
            ) {
                continue
            }

            // as any just to satisfy the type (already validated as string/number/array/object)
            result[key] = value as any
        }

        return result
    }

    /**
     * Get rule data mapped for display in the details drawer (label + value)
     */
    static getRuleDisplayData(
        rule: NetworkStatusRuleRow | null,
    ): Record<
        NetworkStatusRuleDisplayFieldKey,
        INetworkStatusRuleDisplayConfig
    > {
        return {
            id: {
                label: "ID",
                value: rule && "id" in rule ? rule.id.toString() : "",
            },
            version: {
                label: "Rules Version",
                value:
                    rule && "version" in rule && rule.version
                        ? rule.version.version.toString()
                        : "",
            },
            name: {
                label: "Name",
                value: rule?.name ?? "",
                tableWidthClassName: "w-[300px]",
            },
            effectiveDate: {
                label: "Effective Date(s)",
                value: NetworkStatusHelper.getEffectiveDateRangeDisplay(rule),
                tableWidthClassName: "w-[220px]",
            },
            startDate: {
                label: "Start Date",
                value: rule?.startDate
                    ? moment(rule.startDate).format(
                          GENERAL_CONFIG.defaultMomentDateFormatReadable,
                      )
                    : "",
            },
            endDate: {
                label: "End Date",
                value: rule?.endDate
                    ? moment(rule.endDate).format(
                          GENERAL_CONFIG.defaultMomentDateFormatReadable,
                      )
                    : "",
            },
            networkStatus: {
                label: "Network status",
                value: this.getNetworkStatusDisplay(rule),
            },
            payerId: {
                label: "Payer ID",
                value: this.getPayerDisplay(rule),
                tableWidthClassName: "w-[180px]",
                tableCellTruncate: true,
            },
            providerNpi: {
                label: "Provider NPI",
                value: NetworkStatusHelper.getProviderNpiDisplayValue(
                    rule?.providerNpi ?? "",
                ),
            },
            thirdPartyAdministratorId: {
                label: "3rd Party Admin",
                value: this.getTpaDisplay(rule),
                tableWidthClassName: "w-[180px]",
                tableCellTruncate: true,
            },
            insuranceTypes: {
                label: "Insurance Type",
                value: this.getInsuranceTypesDisplay(rule),
                badgeLike: true,
            },
            planTypes: {
                label: "Plan Type",
                value: this.getPlanTypesDisplay(rule),
                badgeLike: true,
            },
            planName: {
                label: "Plan Name",
                value: rule?.planName ?? "",
            },
            groupName: {
                label: "Group Name - Group ID",
                value: rule?.groupName ?? "",
                tableWidthClassName: "w-[180px]",
            },
            state: {
                label: "State",
                value: rule?.state ?? "",
            },
        }
    }

    /**
     * Provider NPI display: digits as entered, or one label for practice-wide (`PRACTICE` / empty after trim).
     */
    private static getProviderNpiDisplayValue(value: string): string {
        const raw = value.trim()

        if (
            !raw ||
            raw.toUpperCase() ===
                NETWORK_STATUS_CONFIG.practiceWideProviderNpiSentinel
        ) {
            return NETWORK_STATUS_CONFIG.practiceWideProviderNpiLabel
        }

        return raw
    }

    /**
     * Get effective date range string for display (e.g. "Jan 1, 2024 - Dec 31, 2024"), or empty string if dates are missing.
     */
    private static getEffectiveDateRangeDisplay(
        rule: NetworkStatusRuleRow | null,
    ): string {
        const { startDate, endDate } = rule || {}

        if (!startDate || !endDate) {
            return ""
        }

        const startFormatted = moment(startDate).format(
            GENERAL_CONFIG.defaultMomentDateFormatReadable,
        )

        const endFormatted = moment(endDate).format(
            GENERAL_CONFIG.defaultMomentDateFormatReadable,
        )

        return `${startFormatted} - ${endFormatted}`
    }

    /**
     * Get display value for network status (e.g. "In-network" instead of "IN") using config mapping, or fallback to raw value or empty string.
     */
    private static getNetworkStatusDisplay(
        rule: NetworkStatusRuleRow | null,
    ): string {
        const mapping = rule?.networkStatus
            ? COVERAGE_CONFIG.payerCoverageCheckNetworkMapped[
                  rule.networkStatus
              ]
            : undefined

        return mapping?.displayValue ?? rule?.networkStatus ?? ""
    }

    /**
     * Get display string for payer
     */
    private static getPayerDisplay(rule: NetworkStatusRuleRow | null): string {
        if (rule && "id" in rule && "payer" in rule) {
            return rule.payer?.insuranceName ?? ""
        }

        return rule?.payerId ?? ""
    }

    /**
     * Get display string for 3rd party payer
     */
    private static getTpaDisplay(rule: NetworkStatusRuleRow | null): string {
        if (rule && "id" in rule && "thirdPartyAdministrator" in rule) {
            return rule.thirdPartyAdministrator?.insuranceName ?? ""
        }

        return rule?.thirdPartyAdministratorId ?? ""
    }

    /**
     * Get display string for insurance types
     */
    private static getInsuranceTypesDisplay(
        rule: NetworkStatusRuleRow | null,
    ): string {
        return rule && Array.isArray(rule.insuranceTypes)
            ? rule.insuranceTypes.join(", ")
            : ""
    }

    /**
     * Get display string for plan types using config mapping, or fallback to raw values; returns empty string if no plan types or invalid format.
     */
    private static getPlanTypesDisplay(
        rule: NetworkStatusRuleRow | null,
    ): string {
        return rule && Array.isArray(rule.planTypes)
            ? rule.planTypes
                  .map((planType: string | AvailablePlanType) => {
                      return planType
                          ? (CHECKER_CONFIG.planTypeMapping[
                                planType as AvailablePlanType
                            ] ?? planType)
                          : ""
                  })
                  .join(", ")
            : ""
    }
}
